<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" aria-label="Close" @click="doConfirm">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">{{ $t('plans.checkout.paymentTitle') }}</h5>
    </div>

    <p>{{ $t('plans.checkout.paymentSubtitle') }}</p>

    <div class="offcanvas-body">
      <div id="checkout">
        <!-- Checkout will insert the payment form here -->
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  p,
  li {
    color:                              var(--Brand-Primary, #1B294A);
    font-size:                          var(--font-size-14);
    font-weight:                        300;
    line-height:                        140%; /* 19.6px */
    margin-bottom:                      26px;
  }

  .offcanvas-header,
  .offcanvas-body {
    padding:                            0;
  }

  .offcanvas-header {
    display:                            block;
  }

  .offcanvas-title {
    color:                              var(--Brand-Primary, #1B294A);
    font-size:                          38px;
    font-weight:                        300;
    line-height:                        normal;
    margin:                             26px 0;
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn {
      font-size:                        var(--font-size-14);
      font-weight:                      700;
      line-height:                      normal;
      letter-spacing:                   4.2px;
      text-align:                       center;
      text-transform:                   uppercase;
      width:                            100%;
      padding:                          20px;
    }

    .btn-success {
      color:                            var(--Shades-W, #FFF);
    }
  }
}
</style>

<script>
import { inject } from "vue";
import { loadStripe } from "@stripe/stripe-js";

export default {
  name: 'BaseGenericOffcanvas',
  props: ['offcanvas_id'],
  data: () => ({
    alert: {
      message: null,
      class: null,
    },
    loading: false,
    offcanvas_checkout: null,
    priceID: null,
    quantity: null,
    clinicUUID: null,
    clinicName: null,
    checkout: null,
    paymentCompleted: false,
    stripe: null,
  }),
  mounted() {
  },
  methods: {
    show(options = {}) {
      this.priceID = options.priceID
      this.quantity = options.quantity
      this.clinicUUID = options.clinicUUID
      this.clinicName = options.clinicName
      this.paymentCompleted = false

      this.offcanvas_checkout = new bootstrap.Offcanvas(document.getElementById(this.offcanvas_id)).show();
      this.initializeCheckout()

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doConfirm() {
      this.offcanvas_checkout = null;
      this.resetCheckout()
      this.resolvePromise(this.paymentCompleted)
    },

    doCancel() {
      this.offcanvas_checkout = null;
      this.resetCheckout()
      this.resolvePromise(this.paymentCompleted)
    },

    async resetCheckout() {
      if (this.loading)
        return

      if (this.checkout) {
        try {
          await this.checkout.destroy()
        } catch (err) {
          console.log('Error resetCheckout', err);
        }
      }

      this.checkout = null
      this.stripe = null

      const checkout = document.getElementById('checkout')
      if (checkout)
        checkout.innerHTML = ''
    },

    async initializeCheckout() {
      if (this.loading || this.checkout)
        return

      this.checkout = null;

      let params = {
        priceID: this.priceID,
        quantity: this.quantity,
        clinicUUID: this.clinicUUID,
        clinicName: this.clinicName
      };

      const currentLocale = this.$i18n.locale === 'pt-br' ? 'pt-BR' : this.$i18n.locale;

      this.loading = true
      try {
        const fetchClientSecret = await this.$stripeService.createCheckoutSession(params);
        let clientSecret = fetchClientSecret.clientSecret;

        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY, {
          locale: currentLocale
        });

        try {
          if (this.stripe && this.stripe.initEmbeddedCheckout) {
            this.checkout = await this.stripe.initEmbeddedCheckout({
              clientSecret,
              onComplete:() =>{
                this.paymentCompleted = true;
              }
            });

            this.$nextTick(() => {
              if (this.checkout)
                this.checkout.mount("#checkout");
            });
          } else {
            console.error("Stripe is not initialized correctly");
          }
        } catch(error) {
          console.error("Stripe is not initialized correctly. Error:", error);
        }
      } catch (err) {
        console.log("Error initializing checkout", err);
        const errorMsg = err.response?.data?.message;

        switch (errorMsg) {
          case 'CLINIC_NOT_FOUND':
            this.$message.popup(this, 'plans.clinicNotFound', 'operationFail', 'error');
            break;

          case 'NOT_ALLOWED':
            this.$message.popup(this, 'plans.notAllowed', 'operationFail', 'error');
            break;

          case 'UNAUTHORIZED_PLAN':
            this.$message.popup(this, 'plans.unauthorizedPlan', 'operationFail', 'error');
            break;

          case 'INVALID_QUANTITY':
            this.$message.popup(this, 'plans.invalidQuantity', 'operationFail', 'error');
            break;

          case 'PRICE_NOT_FOUND':
            this.$message.popup(this, 'plans.priceNotFound', 'operationFail', 'error');
            break;

          default:
            this.$message.popup(this, 'unexpectedError', 'operationFail', 'error');
            break;
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
